import { Dispatch, PayloadAction, createSlice } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { Breed, GetBreedsParams, PaginatedResponse, Pagination, typicodeApi } from 'shared/api';

type TBreedState = {
  breedList: Breed[];
  selectedBreed: Breed | null;
  pagination: Pagination | null;
};

const initialState: TBreedState = {
  breedList: [],
  selectedBreed: null,
  pagination: null,
};

export const breedModel = createSlice({
  name: 'breed',
  initialState,
  reducers: {
    setSelectedBreed: (state, { payload }: PayloadAction<Breed | null>) => {
      state.selectedBreed = payload;
    },
    setBreedList: (state, { payload }: PayloadAction<Breed[]>) => {
      state.breedList = payload;
    },
    setPagination: (state, { payload }: PayloadAction<Pagination>) => {
      state.pagination = payload;
    },
    updateBreedList: (state, { payload }: PayloadAction<Breed>) => {
      state.breedList = state.breedList.map((breed) => (breed.code === payload.code ? payload : breed));
    },
  },
});

export const { setSelectedBreed } = breedModel.actions;

// react-query actions
const ACTS_QUERY_KEY = 'breeds';
export const getBreedsAsync = (params: GetBreedsParams) => (dispatch: Dispatch) =>
  useQuery<AxiosResponse<PaginatedResponse<Breed>>>(
    [ACTS_QUERY_KEY, params],
    () => typicodeApi.breeds.getBreeds(params),
    {
      onSuccess: ({ data }) => {
        dispatch(breedModel.actions.setBreedList(data.data));
        dispatch(breedModel.actions.setPagination(data.pagination));
      },
      onError: () => {},
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    },
  );

const ACTS_QUERY_KEY2 = 'breedById';
export const getBreedByIdAsync = (id: string) => (dispatch: Dispatch) =>
  useQuery<AxiosResponse<Breed>>(ACTS_QUERY_KEY2, () => typicodeApi.breeds.getBreedById(id), {
    onSuccess: ({ data }) => {
      dispatch(breedModel.actions.setSelectedBreed(data));
    },
    onError: () => {},
    refetchOnWindowFocus: false,
  });

// mutations
export const createBreedAsync = () => {
  const queryClient = useQueryClient();
  return useMutation((data: Omit<Breed, '_id'>) => typicodeApi.breeds.postBreed(data), {
    onSuccess: () => {
      toast.success('Breed created successfully!');
      void queryClient.invalidateQueries(ACTS_QUERY_KEY);
    },
    onError: () => {
      toast.error('Error creating breed. Try again!');
    },
  });
};

export const updateBreedAsync = () => {
  const queryClient = useQueryClient();
  return useMutation(({ id, data }: { id: string; data: Breed }) => typicodeApi.breeds.patchBreed(id, data), {
    onSuccess: () => {
      toast.success('Breed updated successfully!');
      void queryClient.invalidateQueries(ACTS_QUERY_KEY);
    },
    onError: () => {
      toast.error('Error updating breed. Try again!');
    },
  });
};

export const deleteBreedAsync = () => {
  const queryClient = useQueryClient();
  return useMutation((id: string) => typicodeApi.breeds.deleteBreedById(id), {
    onSuccess: () => {
      toast.success('Breed deleted successfully!');
      void queryClient.invalidateQueries(ACTS_QUERY_KEY);
    },
    onError: () => {
      toast.error('Error deleting breed. Try again!');
    },
  });
};

// selectors
export const useBreed = () => useSelector((state: RootState) => state.breeds);

export const reducer = breedModel.reducer;
